import { initializeApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator, doc as rawDoc, getDoc as rawGetDoc } from 'firebase/firestore';

// Import auto-tracking Firebase operations
import { 
  collection, 
  doc, 
  addDoc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  arrayUnion,
  onSnapshot,
  serverTimestamp,
  writeBatch
} from './utils/autoTrackingFirebase.js';
import { getAuth, setPersistence, browserLocalPersistence, browserSessionPersistence, GoogleAuthProvider } from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getAnalytics, isSupported } from 'firebase/analytics';
import { getPerformance } from 'firebase/performance';
import { getMessaging, isSupported as isMessagingSupported } from 'firebase/messaging';

// Import auto-tracking Firebase operations
import {
  setGlobalCompanyId,
  getCurrentCompanyId,
  getTrackingStatus,
  stopTracking,
  setTrackingDb
} from './utils/autoTrackingFirebase.js';
// Firebase configuration - mobile optimized
const firebaseConfig = {
  apiKey: "AIzaSyBnR1aGPJYUhW4gfbbel4W0ajluETExD-8",
  authDomain: "acc-app-e5316.firebaseapp.com",
  projectId: "acc-app-e5316",
  storageBucket: "acc-app-e5316.firebasestorage.app",
  messagingSenderId: "447193481869",
  appId: "1:447193481869:android:bf7ba2c9e013aee04e98c4",
  measurementId: "G-PLY7P9M3F8",
  databaseURL: "https://acc-app-e5316-default-rtdb.firebaseio.com"
};

// Initialize Firebase (REAL, no mocks)
let app;
try {
  app = initializeApp(firebaseConfig);
  console.log('✅ Firebase app initialized successfully');
} catch (error) {
  console.error('❌ Firebase initialization failed:', error);
  throw error;
}

// 🗄️ Initialize Firestore Database
const db = getFirestore(app);
setTrackingDb(db);

// 🔐 Initialize Firebase Authentication
const auth = getAuth(app);

// Configure auth persistence for mobile
if (typeof window !== 'undefined' && window.Capacitor) {
  // For Capacitor/mobile apps, use session persistence
  setPersistence(auth, browserSessionPersistence).catch((error) => {
    console.warn('Failed to set auth persistence:', error);
  });
} else {
  // For web, use local persistence
  setPersistence(auth, browserLocalPersistence).catch((error) => {
    console.warn('Failed to set auth persistence:', error);
  });
}

// 🔥 TEMP: Disable Storage to avoid connection issues
const storage = getStorage(app);

// Enable persistence with Capacitor compatibility
const initializeAuthPersistence = async () => {
  try {
    // Check if we're in a Capacitor environment
    const isCapacitor = typeof window !== 'undefined' && window.Capacitor;
    const isElectron = typeof window !== 'undefined' && window.require && window.require('electron');
    
    if (isCapacitor) {
      // For Capacitor apps, use local persistence for better UX
      console.log('📱 Capacitor environment detected, using local persistence');
      await setPersistence(auth, browserLocalPersistence);
    } else if (isElectron) {
      // For Electron apps, use local persistence with custom storage
      console.log('🖥️ Electron environment detected, using local persistence');
      await setPersistence(auth, browserLocalPersistence);
    } else {
      // For web browsers, use local persistence
      console.log('🌐 Web environment detected, using local persistence');
      await setPersistence(auth, browserLocalPersistence);
    }
  } catch (error) {
    console.warn('⚠️ Auth persistence setup failed:', error);
    // Fallback to session persistence
    try {
      await setPersistence(auth, browserSessionPersistence);
    } catch (fallbackError) {
      console.warn('⚠️ Fallback auth persistence also failed:', fallbackError);
    }
  }
};

// Initialize auth persistence
initializeAuthPersistence();

// Google Auth Provider configuration
// NOTE: This provider is ONLY used for web browsers
// Native platforms (Android/iOS) use @capacitor-firebase/authentication plugin instead
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');
googleProvider.addScope('openid');

// Web-only OAuth configuration
// Native platforms bypass this and use the Capacitor plugin
googleProvider.setCustomParameters({
  'prompt': 'select_account'
  // Firebase automatically uses the correct Web OAuth client for browsers
});

// 🔥 TEMP: Disable Functions to avoid connection issues
const functionsClient = getFunctions(app);

// Initialize Firebase Messaging with error handling
let messaging = null;
try {
  if (typeof window !== 'undefined') {
    // Check if we're in a Capacitor environment (mobile)
    const isCapacitor = typeof window !== 'undefined' && window.Capacitor;
    
    if (isCapacitor) {
      // For mobile apps, initialize messaging directly
      messaging = getMessaging(app);
      console.log('✅ Firebase Messaging initialized for mobile');
    } else if (isMessagingSupported()) {
      // For web browsers, check support first
      messaging = getMessaging(app);
      console.log('✅ Firebase Messaging initialized for web');
    } else {
      console.log('📱 Firebase Messaging not supported in this browser environment');
    }
  } else {
    console.log('📱 Firebase Messaging not available (no window object)');
  }
} catch (error) {
  console.warn('⚠️ Firebase Messaging initialization failed:', error);
  // Don't throw the error, just log it to prevent app crashes
}

// Initialize Analytics with proper offline handling
let analytics = null;
let performance = null;

// Check if we're online and have valid configuration
const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
const hasValidAppId = typeof firebaseConfig.appId === 'string' && firebaseConfig.appId.includes(':');

// Initialize Analytics/Performance only when online and with valid config
if (isOnline && hasValidAppId && typeof window !== 'undefined') {
  // Initialize Analytics with error handling
  isSupported()
    .then(yes => {
      if (yes) {
        try {
          analytics = getAnalytics(app);
          console.log('✅ Firebase Analytics initialized successfully');
        } catch (error) {
          console.warn('⚠️ Firebase Analytics initialization failed:', error.message);
          analytics = null;
        }
      }
    })
    .catch(error => {
      console.warn('⚠️ Firebase Analytics support check failed:', error.message);
      analytics = null;
    });

  // Initialize Performance with error handling
  try {
    performance = getPerformance(app);
    console.log('✅ Firebase Performance initialized successfully');
  } catch (error) {
    console.warn('⚠️ Firebase Performance initialization failed:', error.message);
    performance = null;
  }
} else {
  console.log('📴 Firebase Analytics/Performance disabled (offline or invalid config)');
  analytics = null;
  performance = null;
}

// Listen for online/offline events to handle connectivity changes
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log('🌐 App is back online');
    // Re-initialize analytics when back online
    if (hasValidAppId && !analytics) {
      isSupported()
        .then(yes => {
          if (yes) {
            try {
              analytics = getAnalytics(app);
              console.log('✅ Firebase Analytics re-initialized after coming online');
            } catch (error) {
              console.warn('⚠️ Firebase Analytics re-initialization failed:', error.message);
            }
          }
        })
        .catch(error => {
          console.warn('⚠️ Firebase Analytics support check failed on reconnect:', error.message);
        });
    }
  });

  window.addEventListener('offline', () => {
    console.log('📴 App is offline');
    // Don't disable analytics when going offline, just log it
  });
}

// 🔥 ENHANCED DATABASE LAYER WITH OFFLINE CAPABILITIES
export const appDb = {
  // Device management collections
  devices: (companyId) => {
    if (db && db.type !== 'mock-firestore') {
      return collection(db, 'companies', companyId, 'devices');
    }
    return { type: 'mock-collection', path: `companies/${companyId}/devices` };
  },
  
  deviceCompanyMapping: (deviceId) => {
    if (db && db.type !== 'mock-firestore') {
      return doc(db, 'device_company_mapping', deviceId);
    }
    return { type: 'mock-doc', path: `device_company_mapping/${deviceId}` };
  },
  
  userCompanyMapping: (userId) => {
    if (db && db.type !== 'mock-firestore') {
      return doc(db, 'user_company_mapping', userId);
    }
    return { type: 'mock-doc', path: `user_company_mapping/${userId}` };
  },
  
  syncQueue: (companyId) => {
    if (db && db.type !== 'mock-firestore') {
      return collection(db, 'companies', companyId, 'sync_queue');
    }
    return { type: 'mock-collection', path: `companies/${companyId}/sync_queue` };
  },
  
  conflictReports: (companyId) => {
    if (db && db.type !== 'mock-firestore') {
      return collection(db, 'companies', companyId, 'conflict_reports');
    }
    return { type: 'mock-collection', path: `companies/${companyId}/conflict_reports` };
  },

  // Company data collections with null checks
  companyData: (companyId) => {
    if (db && db.type !== 'mock-firestore') {
      return collection(db, 'companies', companyId);
    }
    return { type: 'mock-collection', path: `companies/${companyId}` };
  },
  companyInfo: (companyId) => {
    if (db && db.type !== 'mock-firestore') {
      return doc(db, 'companies', companyId);
    }
    return { type: 'mock-doc', path: `companies/${companyId}` };
  },
  employees: (companyId) => {
    if (db && db.type !== 'mock-firestore') {
      return collection(db, 'companies', companyId, 'employees');
    }
    return { type: 'mock-collection', path: `companies/${companyId}/employees` };
  },
  items: (companyId) => {
    if (db && db.type !== 'mock-firestore') {
      return collection(db, 'companies', companyId, 'items');
    }
    return { type: 'mock-collection', path: `companies/${companyId}/items` };
  },
  parties: (companyId) => {
    if (db && db.type !== 'mock-firestore') {
      return collection(db, 'companies', companyId, 'parties');
    }
    return { type: 'mock-collection', path: `companies/${companyId}/parties` };
  },
  salesBills: (companyId) => {
    if (db && db.type !== 'mock-firestore') {
      return collection(db, 'companies', companyId, 'sales_bills');
    }
    return { type: 'mock-collection', path: `companies/${companyId}/sales_bills` };
  },
  purchaseBills: (companyId) => {
    if (db && db.type !== 'mock-firestore') {
      return collection(db, 'companies', companyId, 'purchase_bills');
    }
    return { type: 'mock-collection', path: `companies/${companyId}/purchase_bills` };
  },
  expenses: (companyId) => {
    if (db && db.type !== 'mock-firestore') {
      return collection(db, 'companies', companyId, 'expenses');
    }
    return { type: 'mock-collection', path: `companies/${companyId}/expenses` };
  },
  payments: (companyId) => {
    if (db && db.type !== 'mock-firestore') {
      return collection(db, 'companies', companyId, 'payments');
    }
    return { type: 'mock-collection', path: `companies/${companyId}/payments` };
  },
  reports: (companyId) => {
    if (db && db.type !== 'mock-firestore') {
      return collection(db, 'companies', companyId, 'reports');
    }
    return { type: 'mock-collection', path: `companies/${companyId}/reports` };
  },
  
  // User authentication and company switching with null checks
  userCompanies: (userId) => {
    if (db && db.type !== 'mock-firestore') {
      return doc(db, 'user_companies', userId);
    }
    return { type: 'mock-doc', path: `user_companies/${userId}` };
  },
  userSessions: () => {
    if (db && db.type !== 'mock-firestore') {
      return collection(db, 'user_sessions');
    }
    return { type: 'mock-collection', path: 'user_sessions' };
  },
  mpinVerifications: () => {
    if (db && db.type !== 'mock-firestore') {
      return collection(db, 'mpin_verifications');
    }
    return { type: 'mock-collection', path: 'mpin_verifications' };
  },
  accessLogs: () => {
    if (db && db.type !== 'mock-firestore') {
      return collection(db, 'access_logs');
    }
    return { type: 'mock-collection', path: 'access_logs' };
  },
  
  // 🔥 TEMP: Utility methods with null checks
  getCompanyInfo: async (companyId) => {
    try {
      if (!db || db.type === 'mock-firestore') {
        console.log('🔥 TEMP: Firestore not available, returning mock data');
        return { id: companyId, name: 'Mock Company', status: 'active' };
      }
      const docRef = doc(db, 'companies', companyId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error fetching company info:', error);
      // Return mock data for testing
      return { id: companyId, name: 'Mock Company', status: 'active' };
    }
  },
  
  getEmployee: async (companyId, employeeId) => {
    try {
      if (!db || db.type === 'mock-firestore') {
        console.log('🔥 TEMP: Firestore not available, returning mock data');
        return { id: employeeId, name: 'Mock Employee', role: 'employee' };
      }
      const docRef = doc(db, 'companies', companyId, 'employees', employeeId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error fetching employee:', error);
      // Return mock data for testing
              return { id: employeeId, name: 'Mock Employee', role: 'employee' };
    }
  },
  
  getUserCompanies: async (userId) => {
    try {
      if (!db || db.type === 'mock-firestore') {
        console.log('🔥 TEMP: Firestore not available, returning mock data');
        return { id: userId, companies: ['company_1', 'company_2'] };
      }
      const docRef = doc(db, 'user_companies', userId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error fetching user companies:', error);
      // Return mock data for testing
      return { id: userId, companies: ['company_1', 'company_2'] };
    }
  },
  
  // Device management methods
  registerDevice: async (deviceInfo) => {
    try {
      if (!db || db.type === 'mock-firestore') {
        console.log('🔥 TEMP: Firestore not available, returning mock device ID');
        return { success: true, deviceId: 'mock_device_' + Date.now() };
      }
      const deviceRef = doc(appDb.devices());
      const deviceData = {
        ...deviceInfo,
        createdAt: new Date(),
        status: 'pending_approval',
        lastSeen: new Date()
      };
      await setDoc(deviceRef, deviceData);
      return { success: true, deviceId: deviceRef.id };
    } catch (error) {
      console.error('Error registering device:', error);
      return { success: false, error: error.message };
    }
  },
  
  getDeviceInfo: async (deviceId, companyId) => {
    try {
      if (!db || db.type === 'mock-firestore') {
        return { id: deviceId, name: 'Mock Device', status: 'active' };
      }
      
      if (!companyId) {
        console.error('Company ID required for device lookup');
        return null;
      }
      
      // Search for device by deviceId field in the company's devices collection
      const devicesRef = collection(db, 'companies', companyId, 'devices');
      const q = query(devicesRef, where('deviceId', '==', deviceId));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        console.log('Device not found in company devices:', deviceId);
        return null;
      }
      
      const deviceDoc = snapshot.docs[0];
      return { id: deviceDoc.id, ...deviceDoc.data() };
    } catch (error) {
      console.error('Error fetching device info:', error);
      return null;
    }
  },
  
  updateDeviceStatus: async (deviceId, status, companyId) => {
    try {
      if (!db || db.type === 'mock-firestore') {
        return { success: true };
      }
      
      // First, find the device document by deviceId field
      const devicesRef = collection(db, 'companies', companyId, 'devices');
      const q = query(devicesRef, where('deviceId', '==', deviceId));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return { success: false, error: 'Device not found' };
      }
      
      // Get the first matching document
      const deviceDoc = snapshot.docs[0];
      const deviceRef = doc(db, 'companies', companyId, 'devices', deviceDoc.id);
      
      await updateDoc(deviceRef, { 
        status, 
        authorizedAt: status === 'approved' ? new Date() : null,
        lastSeen: new Date() 
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating device status:', error);
      return { success: false, error: error.message };
    }
  },
  
  getCompanyDevices: async (companyId) => {
    try {
      if (!db || db.type === 'mock-firestore') {
        return [];
      }
      const devicesRef = appDb.devices(companyId);
      const snapshot = await getDocs(devicesRef);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching company devices:', error);
      return [];
    }
  },

  registerDevice: async (deviceInfo, companyId) => {
    try {
      if (!db || db.type === 'mock-firestore') {
        return { success: true, deviceId: 'mock-device-id' };
      }
      
      const devicesRef = appDb.devices(companyId);
      
      // Remove any undefined fields and ensure all required fields are present
      const cleanDeviceInfo = {
        deviceId: deviceInfo.deviceId,
        userId: deviceInfo.userId,
        name: deviceInfo.name || 'Unknown Device',
        type: deviceInfo.type || 'unknown',
        userAgent: deviceInfo.userAgent || navigator.userAgent,
        platform: deviceInfo.platform || navigator.platform,
        language: deviceInfo.language || navigator.language,
        timezone: deviceInfo.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        screenResolution: deviceInfo.screenResolution || `${window.screen.width}x${window.screen.height}`,
        status: deviceInfo.status || 'pending_approval',
        authorizedAt: null,
        lastSeen: new Date(),
        createdAt: new Date()
      };
      
      console.log('Registering device with info:', cleanDeviceInfo);
      
      const docRef = await addDoc(devicesRef, cleanDeviceInfo);
      
      return { success: true, deviceId: docRef.id };
    } catch (error) {
      console.error('Error registering device:', error);
      return { success: false, error: error.message };
    }
  }
};

// 🔐 ENHANCED AUTHENTICATION HELPERS
export const appAuth = {
  // User roles in company
  roles: {
    OWNER: 'owner',
    MANAGER: 'manager',
    EMPLOYEE: 'employee',
    VIEWER: 'viewer'
  },
  
  // Check if user is company owner
  isCompanyOwner: async (companyId) => {
    try {
      const companyInfo = await appDb.getCompanyInfo(companyId);
      return companyInfo && companyInfo.owner && companyInfo.owner.userId === auth?.currentUser?.uid;
    } catch (error) {
      console.error('Error checking company ownership:', error);
      return false;
    }
  },
  
  // Check if user has specific permission
  hasPermission: async (companyId, permission) => {
    try {
      if (!auth?.currentUser) return false;
      
      // Check if owner
      const isOwner = await appAuth.isCompanyOwner(companyId);
      if (isOwner) return true;
      
      // Check employee permissions
      const employee = await appDb.getEmployee(companyId, auth.currentUser.uid);
      return employee && employee.permissions && employee.permissions.includes(permission);
    } catch (error) {
      console.error('Error checking permissions:', error);
      return false;
    }
  }
};

// 🔥 FIREBASE STORAGE HELPERS - REAL FIREBASE ONLY
export const appStorage = {
  // Upload company assets to Firebase Storage
  uploadCompanyAsset: async (file, companyId, category) => {
    try {
      if (!storage) {
        throw new Error('Firebase Storage not initialized. Please check your internet connection.');
      }
      
      // Check if user is authenticated
      if (!auth?.currentUser) {
        throw new Error('User not authenticated. Please login again.');
      }
      
      // Create unique filename to avoid conflicts
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `companies/${companyId}/${category}/${fileName}`);
      
      console.log('📤 Uploading to Firebase Storage:', { 
        companyId, 
        category, 
        fileName,
        user: auth?.currentUser?.uid,
        fileSize: file.size,
        fileType: file.type
      });
      
      // Try to upload with retry mechanism
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries) {
        try {
          console.log(`📤 Upload attempt ${retryCount + 1}/${maxRetries}`);
          const metadata = {
            customMetadata: {
              ownerUid: auth.currentUser.uid,
              companyId,
            },
          };
          const snapshot = await uploadBytes(storageRef, file, metadata);
          const downloadURL = await getDownloadURL(snapshot.ref);
          
          console.log('✅ Firebase Storage upload successful:', downloadURL);
          return downloadURL;
        } catch (uploadError) {
          retryCount++;
          console.error(`❌ Upload attempt ${retryCount} failed:`, uploadError);
          
          if (retryCount >= maxRetries) {
            throw uploadError;
          }
          
          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        }
      }
      
    } catch (error) {
      console.error('❌ Firebase Storage upload failed:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      throw new Error(`Failed to upload to Firebase Storage: ${error.message}`);
    }
  },
  
  // Upload employee documents to Firebase Storage
  uploadEmployeeDocument: async (file, companyId, employeeId, documentType) => {
    try {
      if (!storage) {
        throw new Error('Firebase Storage not initialized. Please check your internet connection.');
      }
      
      // Create unique filename to avoid conflicts
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `companies/${companyId}/employees/${employeeId}/${documentType}/${fileName}`);
      
      console.log('📤 Uploading employee document to Firebase Storage:', { companyId, employeeId, documentType, fileName });
      
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      console.log('✅ Employee document upload successful:', downloadURL);
      return downloadURL;
      
    } catch (error) {
      console.error('❌ Employee document upload failed:', error);
      throw new Error(`Failed to upload employee document: ${error.message}`);
    }
  },
  
  // Delete company asset from Firebase Storage
  deleteCompanyAsset: async (filePath) => {
    try {
      if (!storage) {
        throw new Error('Firebase Storage not initialized. Please check your internet connection.');
      }
      
      console.log('🗑️ Deleting from Firebase Storage:', filePath);
      const fileRef = ref(storage, filePath);
      await deleteObject(fileRef);
      
      console.log('✅ File deleted successfully from Firebase Storage');
      return true;
      
    } catch (error) {
      console.error('❌ Firebase Storage delete failed:', error);
      throw new Error(`Failed to delete from Firebase Storage: ${error.message}`);
    }
  }
};

// Emulators optional (disabled by default)

// Dev-only usage analytics console (not exposed in customer UI)
const getUsageMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const resolveDevCompanyId = (companyId) =>
  companyId ||
  (typeof localStorage !== 'undefined' && localStorage.getItem('acctoo_last_company')) ||
  null;

function isAcctooDebugConsoleEnabled() {
  if (typeof window === 'undefined') return false;
  if (process.env.REACT_APP_ACCTOO_DEBUG === 'true') return true;
  if (process.env.NODE_ENV === 'development') return true;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

if (isAcctooDebugConsoleEnabled()) {
  window.__acctoo = {
    /** Current billing counter vs usage doc for active month (raw Firestore reads). */
    async getOperationCount(companyId) {
      const cid = resolveDevCompanyId(companyId);
      if (!cid) {
        throw new Error('No company id — select a company or pass getOperationCount("COMP…")');
      }
      const month = getUsageMonthKey();
      const [subSnap, usageSnap] = await Promise.all([
        rawGetDoc(rawDoc(db, 'companies', cid, 'subscription_details', 'subscription')),
        rawGetDoc(rawDoc(db, 'companies', cid, 'usage', month))
      ]);
      const subData = subSnap.exists() ? subSnap.data() : null;
      const usageMonth = usageSnap.exists() ? usageSnap.data() : null;
      const currentMonthUsage = subData?.currentMonthUsage || {};
      const entitlements = subData?.totalEntitlements || subData?.basePlan?.limits || {};
      return {
        companyId: cid,
        subscription: {
          firebase_operations: currentMonthUsage.firebase_operations ?? 0,
          limit: entitlements.firebase_operations ?? 0,
          storage_mb: currentMonthUsage.storage_mb ?? 0,
          storage_limit_mb: entitlements.storage_mb ?? 0
        },
        usageMonthDoc: usageMonth
          ? {
              month: usageMonth.month || month,
              operations: usageMonth.operations,
              breakdown: usageMonth.breakdown,
              dailyUsage: usageMonth.dailyUsage,
              audit: {
                lastRecalculatedFromDaily: usageMonth.lastRecalculatedFromDaily,
                recalculatedSinceDate: usageMonth.recalculatedSinceDate,
                clearedDailyBefore: usageMonth.clearedDailyBefore
              },
              lastUpdated: usageMonth.lastUpdated
            }
          : null
      };
    },
    /** Lifetime tracked ops across all usage/{YYYY-MM} docs. */
    async getAllTimeOperationCount(companyId) {
      const cid = resolveDevCompanyId(companyId);
      if (!cid) throw new Error('No company id');
      const { getLifetimeTrackedOperations } = await import('./utils/usageTracker');
      return getLifetimeTrackedOperations(db, cid);
    },
    /** Full analysis report: quality flags, outliers, billing drift, buffer state. */
    async getUsageAnalysisReport(companyId) {
      const cid = resolveDevCompanyId(companyId);
      if (!cid) throw new Error('No company id');
      const { getUsageAnalysisReport } = await import('./utils/usageTrackingAnalytics');
      const { getTrackingBufferState } = await import('./utils/usageTracker');
      return getUsageAnalysisReport(db, cid, { bufferState: getTrackingBufferState() });
    },
    /** Runtime tracking + buffer + current month doc (flushes pending buffer first). */
    async getTrackingDebugState(companyId) {
      const { getTrackingStatus } = await import('./utils/autoTrackingFirebase.js');
      const { getTrackingBufferState, flushOperationBuffer } = await import('./utils/usageTracker');
      const cid = resolveDevCompanyId(companyId);
      const status = getTrackingStatus();
      const bufferState = getTrackingBufferState();
      if (cid && bufferState.buffers[cid]?.total > 0) {
        await flushOperationBuffer(db, cid);
      }
      const month = getUsageMonthKey();
      const usageSnap = cid ? await rawGetDoc(rawDoc(db, 'companies', cid, 'usage', month)) : null;
      return {
        companyId: cid,
        tracking: status,
        buffer: getTrackingBufferState(),
        currentMonthUsageDoc: usageSnap?.exists() ? usageSnap.data() : null
      };
    },
    /** Admin repair: rebuild month from daily log (optional cutoff / clear earlier days). */
    async syncMonthFromDaily(companyId, monthId, sinceDate, clearBeforeDate = false) {
      const cid = resolveDevCompanyId(companyId);
      if (!cid || !monthId) {
        throw new Error('Provide companyId and monthId (YYYY-MM)');
      }
      const { recalculateUsageMonthFromDaily, clearUsageBeforeDate } = await import('./utils/usageTracker');
      if (clearBeforeDate) {
        if (!sinceDate) throw new Error('sinceDate required when clearBeforeDate is true');
        return clearUsageBeforeDate(db, cid, monthId, sinceDate);
      }
      return recalculateUsageMonthFromDaily(db, cid, monthId, { sinceDate: sinceDate || null });
    }
  };
  console.info(
    '[AccToo] Debug console ready: __acctoo.getUsageAnalysisReport(), syncMonthFromDaily(companyId, monthId, sinceDate?, clearBefore?)'
  );
}

// Enhanced error handling for Firebase operations
const handleFirebaseError = (error, operation = 'Firebase operation') => {
  console.error(`❌ ${operation} failed:`, error);
  
  // Handle specific error types
  if (error.code === 'permission-denied') {
    console.error('🔒 Permission denied - check security rules');
  } else if (error.code === 'unavailable') {
    console.error('🌐 Service unavailable - check internet connection');
  } else if (error.code === 'unauthenticated') {
    console.error('🔐 User not authenticated');
  }
  
  return error;
};

// Export enhanced Firebase services
export {
  db, 
  auth, 
  app, 
  functionsClient, 
  googleProvider, 
  storage, 
  analytics, 
  performance,
  messaging,
  handleFirebaseError,
  // Auto-tracking functions
  setGlobalCompanyId,
  getCurrentCompanyId,
  getTrackingStatus,
  stopTracking,
  // Auto-tracking Firebase operations
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  arrayUnion,
  onSnapshot,
  serverTimestamp,
  writeBatch
};
